import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { BuildingView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { BuildingService } from '@http/clients/building.service';
import { Building } from '@models/Building';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

@Component({
    selector: 'app-modal-add-building',
    templateUrl: './modal-add-building.component.html',
    styleUrls: ['./modal-add-building.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalAddBuildingComponent extends ModalComponentBase<undefined, BuildingView> implements OnInit {
    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    building: Building;

    isReadOnly: boolean;

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly buildingService: BuildingService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.building = new Building();
        this.isReadOnly = false;
        this.configItems = [];
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.configItems = [
            {
                label: 'Name',
                componentId: EditorType.textInput,
                fieldName: 'name',
                bindContext: this.building,
                isReadOnly: this.isReadOnly,
                validations: [
                    { type: ValidatorType.required },
                    { type: ValidatorType.string, args: { maxLength: 100 } }
                ],
                additionalInfo: {
                    shouldTrimSpaces: true
                }
            },
            {
                label: 'Class',
                componentId: EditorType.buildingClass,
                fieldName: 'buildingBuildingClasses',
                bindContext: this.building,
                isReadOnly: this.isReadOnly
            },
            {
                label: 'Website',
                componentId: EditorType.link,
                fieldName: 'website',
                bindContext: this.building,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 500 } }]
            },
            {
                label: 'Full Address',
                componentId: EditorType.address,
                fieldName: 'addressLines',
                bindContext: this.building,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.required }]
            }
        ];
    }

    private validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveBuilding(): Promise<void> {
        if (!this.validate()) return;

        await this.awaiter.awaitAction(
            'Saving Building...',
            async () => {
                const response = await this.buildingService.save(this.building);
                if (response) this.snackbarNotificationService.success('Building created successfully.');
                this.close(response);
            },
            (loading) => (this.isLoading = loading)
        );
    }
}
